import {
  CustomColumnHeader,
  CustomColumnItem,
  getQualifyingColumnType,
  ID,
  LineItem,
  SupplierQualAnswer,
  getYesNoQualQuestionType,
  validSupplierLIQTypes,
} from 'types';
import { checkSameId } from '@components/bid_request/details/utils';
import { getDescriptiveAndQualifyingColumns } from './columnHelpers';
import { getAnswerValue } from './getAnswerValue';

interface BuildCustomColumnsParams {
  columnIds: ID[];
  lineItem: LineItem;
}

const getValue = (questionType?: string, value?: unknown): string => {
  // For yes/no questions, preserve numeric values (0 or 1)
  if (questionType === getYesNoQualQuestionType()) {
    return Number.isNaN(value) ? '' : String(value);
  }
  // For text/numeric questions, render whatever saved value we have as text
  return value == null ? '' : String(value);
};

/**
 * Builds custom column data for a line item by extracting display text from custom column items
 * For supplier view, also checks for saved qual answer values for qualifying columns
 * Shared between buyer and supplier table data generation
 */
export const buildCustomColumns = ({
  columnIds,
  lineItem,
}: BuildCustomColumnsParams): string[] => (
  columnIds.map((headerId: ID) => {
    const item = lineItem.custom_column_items?.find((cci: CustomColumnItem) => (
      checkSameId(cci.custom_column_header_id, headerId)
    ));

    return item?.display_text || '';
  })
);

interface BuildQuoteCustomColumnsParams {
  columnIds: ID[];
  headers?: CustomColumnHeader[];
  lineItem: LineItem;
  lineItemQuestionAnswers?: Record<ID, string>;
}

/**
 * Builds custom column data for a line item by extracting display text from custom column items
 * For supplier view, also checks for saved qual answer values for qualifying columns
 * Shared between buyer and supplier table data generation
 */
export const buildQuoteCustomColumns = ({
  columnIds,
  headers,
  lineItem,
  lineItemQuestionAnswers,
}: BuildQuoteCustomColumnsParams): string[] => {
  const qualifyingType = getQualifyingColumnType();
  const {
    custom_column_items: customColumnItems = [],
    supplier_qual_answers: lineItemAnswers = [],
  } = lineItem;

  return columnIds.map((headerId: ID): string => {
    const item: CustomColumnItem | undefined = customColumnItems
      .find((cci: CustomColumnItem) => (
        checkSameId(cci.custom_column_header_id, headerId)
      ));
    const displayText = item?.display_text || '';

    if (!headers?.length) return displayText;
    // For supplier view, check if this is a qualifying column with a saved answer
    const header = headers.find((h: CustomColumnHeader) => (
      checkSameId(h.id, headerId)
    ));

    if (header?.column_type === qualifyingType) {
      const { id: questionId, question_type: questionType } = header.qual_question || {};
      const isValueQuestion = questionType ? validSupplierLIQTypes.has(questionType) : false;

      if (isValueQuestion) {
        const value: string | undefined = questionId ? lineItemQuestionAnswers?.[questionId] : undefined;

        // Check if value is explicitly set in overlay (including empty strings)
        // Use !== undefined instead of truthy check to allow empty strings to display immediately
        if (value !== undefined) {
          return getValue(questionType, value);
        }
        const savedAnswer = lineItemAnswers.find((sqa: SupplierQualAnswer): boolean => (
          checkSameId(sqa.qual_question_id, header.qual_question?.id)
        ));

        if (savedAnswer) {
          const savedValue = getAnswerValue(savedAnswer, false, questionType);

          if (savedValue !== undefined && savedValue !== null && savedValue !== '') {
            return getValue(questionType, savedValue);
          }
        }

        // For value questions (text, numeric, yes/no) that have no answers, return empty string instead the display_text of the Qual Answer
        return '';
      }
    }

    return displayText;
  });
};

/**
 * Gets the appropriate column IDs based on whether this is for buyer or supplier view
 * Handles the different column selection strategies between views
 */
export const getColumnIds = (
  headers: CustomColumnHeader[],
  isSupplier: boolean,
  maxDescriptiveColumnCount?: number
): ID[] => {
  if (isSupplier) {
    return getDescriptiveAndQualifyingColumns({
      headers,
      isSupplier,
      maxDescriptiveColumnCount: 0,
    }).map((header: CustomColumnHeader) => header.id);
  }

  return getDescriptiveAndQualifyingColumns({
    headers,
    isSupplier,
    maxDescriptiveColumnCount: maxDescriptiveColumnCount || 0
  }).map((header: CustomColumnHeader) => header.id);
};
